/**
 * API client for geo detection and waitlist endpoints.
 * Uses the same /api/v1 base URL as the rest of the app (proxied in dev).
 */

const API_BASE = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/$/, '');

export interface GeoCountryResponse {
  country: string | null;
  is_supported: boolean;
  supported_countries: string[];
}

export interface WaitlistPayload {
  email: string;
  petName: string;
  animalType: 'perro' | 'gato';
  country: string;
  petWeightKg?: number;
  /** Free-form origin marker (e.g. "salud-tier2 | pathology=renal") so the
   *  admin can tell apart leads coming from different funnel touchpoints. */
  context?: string;
}

export interface WaitlistResponse {
  id: string;
  saved: boolean;
  /** True when the (email, country) was already on the list and got refreshed
   *  instead of created — lets the UI show "ya estabas en la lista". */
  already_registered?: boolean;
}

/**
 * Detect the visitor's country from the backend (IP-based).
 * Accepts an optional override code (e.g. ?country=XX in tests).
 * Throws on network/server error — callers should handle gracefully.
 */
export async function getCountry(countryOverride?: string | null, signal?: AbortSignal): Promise<GeoCountryResponse> {
  const url = new URL(`${API_BASE}/geo/country`, window.location.href);
  if (countryOverride) {
    url.searchParams.set('country', countryOverride);
  }
  const res = await fetch(url.toString(), { signal });
  if (!res.ok) {
    throw new Error(`geo/country error: ${res.status}`);
  }
  return res.json() as Promise<GeoCountryResponse>;
}

/**
 * Submit the country-gate waitlist form.
 * Returns the created entry on success (201); throws on error.
 */
export async function joinWaitlist(payload: WaitlistPayload): Promise<WaitlistResponse> {
  const res = await fetch(`${API_BASE}/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`waitlist error: ${res.status} ${text}`);
  }
  return res.json() as Promise<WaitlistResponse>;
}
